import React from 'react';

/**
 * The only button in the app. Every size clears the 44px touch minimum, so a
 * `sm` button is visually smaller but still thumb-safe on a phone.
 */

const VARIANTS = {
  primary: 'bg-primary text-primary-fg hover:bg-primary-hover shadow-sm',
  accent: 'bg-accent text-accent-fg hover:bg-accent-hover shadow-sm',
  secondary: 'bg-surface text-foreground border border-border-strong hover:bg-muted',
  ghost: 'bg-transparent text-muted-fg hover:bg-muted hover:text-foreground',
  danger: 'bg-danger text-white hover:bg-danger-fg shadow-sm',
  'danger-soft': 'bg-danger-soft text-danger-fg border border-danger/20 hover:bg-danger/10',
  success: 'bg-success text-white hover:bg-success-fg shadow-sm',
};

const SIZES = {
  sm: 'min-h-11 px-3 text-sm gap-1.5',
  md: 'min-h-11 px-4 text-sm gap-2',
  lg: 'min-h-12 px-6 text-base gap-2',
};

const Spinner = () => (
  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

const Button = React.forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon: Icon,
    iconRight: IconRight,
    fullWidth = false,
    className = '',
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={[
        'inline-flex items-center justify-center rounded-md font-medium',
        'transition-colors duration-base ease-out cursor-pointer select-none',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant] || VARIANTS.primary,
        SIZES[size] || SIZES.md,
        fullWidth ? 'w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {loading ? <Spinner /> : Icon ? <Icon style={{ fontSize: 18 }} aria-hidden="true" /> : null}
      {children}
      {IconRight && !loading ? <IconRight style={{ fontSize: 18 }} aria-hidden="true" /> : null}
    </button>
  );
});

/**
 * Square icon-only button. `label` is required — it becomes the accessible name,
 * since there is no visible text to fall back on.
 */
export const IconButton = React.forwardRef(function IconButton(
  { icon: Icon, label, variant = 'ghost', className = '', ...rest },
  ref
) {
  return (
    <Button
      ref={ref}
      variant={variant}
      aria-label={label}
      title={label}
      className={`w-11 !px-0 ${className}`}
      {...rest}
    >
      <Icon style={{ fontSize: 20 }} aria-hidden="true" />
    </Button>
  );
});

export default Button;
